import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { createAssignment } from "../../worksheetSlice";

import Modal from "../../../../components/Modal";

import TypeSelectionPage from "../common/TypeSelectionPage/TypeSelectionPage";
import ParagraphSelectionPage from "./ParagraphSelectionPage/ParagraphSelectionPage";
import DateSelectionPage from "./DateSelectionPage/DateSelectionPage";
import StudentSelectionPage from "./StudentSelectionPage/StudentSelectionPage";
import TitleSelectionPage from "./TitleSelectionPage/TitleSelectionPage";
const WorksheetModal = ({ onClose }) => {
  const dispatch = useDispatch();

  const { worksheet } = useSelector((state) => state.worksheet);
  const { user } = useSelector((state) => state.auth);

  const [currentStep, setCurrentStep] = useState("title");

  // ===== [Step 1 상태] =====
  const [selectedParagraphs, setSelectedParagraphs] = useState(() => {
    // For sentence type, data is an object with documents, not an array
    if (worksheet?.type === "sentence") {
      return [0]; // Only one paragraph for sentence type
    }
    return Array.isArray(worksheet?.data) ? worksheet.data.map((_, i) => i) : [0];
  });
  const [selectedTypes, setSelectedTypes] = useState([]);

  // ===== [Step 2 상태] =====
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [hideAfterClosed, setHideAfterClosed] = useState(true);

  // ===== [Step 3 상태] =====
  const [selectedStudents, setSelectedStudents] = useState([]);

  // ===== [복습 설정 상태] =====
  const [isRetryEnabled, setIsRetryEnabled] = useState(true);
  const [isAutoRetryEnabled, setIsAutoRetryEnabled] = useState(false);
  const [maxRetries, setMaxRetries] = useState(3);

  // Check if user has basic plan and prevent modal access
  useEffect(() => {
    if (user.plan === "basic") {
      alert("기본 플랜에서는 과제를 출제할 수 없습니다. Plus 플랜으로 업그레이드해주세요.");
      onClose();
    }
  }, [user.plan, onClose]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleStep = (step) => {
    setCurrentStep(step);
  };

  const handleTypeClick = (type) => {
    setSelectedTypes((prev) => {
      if (prev.includes(type)) {
        return prev.filter((item) => item !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleCreateAssignment = async () => {
    let worksheetData = [];
    if (worksheet.type === "comprehensive") {
      const examIndex =
        selectedTypes.find((type) => type.key === "comprehensive")?.index || 1;
      worksheetData = worksheet.data[examIndex - 1];
    } else {
      worksheetData = worksheet.data;
    }

    // Extract max review count and review enabled for vocabulary and paragraph
    let finalMaxRetries = undefined;
    let finalIsRetryEnabled = undefined;
    let finalIsAutoRetryEnabled = undefined;
    if (worksheet.type === "vocabulary") {
      const examType = selectedTypes.find((type) => type.key === "exam");
      finalMaxRetries = examType?.maxRetries ?? 3;
      finalIsRetryEnabled = examType?.isRetryEnabled !== false;
      finalIsAutoRetryEnabled = examType?.isAutoRetryEnabled !== false;
    } else if (worksheet.type === "paragraph") {
      // For paragraph, use state variables that can be modified in the UI
      finalMaxRetries = maxRetries;
      finalIsRetryEnabled = isRetryEnabled;
      finalIsAutoRetryEnabled = isAutoRetryEnabled;
    }

    const data = {
      students: selectedStudents,
      closed_at: selectedDate,
      data: {
        // For sentence type, worksheetData is an object {version, documents, sentence_list}
        // For other types, it's an array that needs filtering
        paragraphs: Array.isArray(worksheetData)
          ? worksheetData.filter((_, i) => selectedParagraphs.includes(i))
          : worksheetData,
        types: selectedTypes,
      },
      type: worksheet.type,
      title: worksheet.title,
      author_name: worksheet.author_name,
      hide_after_closed: hideAfterClosed,
      ginedu_id: worksheet.id,
      // Add these fields for vocabulary and paragraph assignments
      ...(["vocabulary", "paragraph"].includes(worksheet.type)
        ? {
            max_retries: finalMaxRetries,
            is_retry_enabled: finalIsRetryEnabled,
            is_auto_retry_enabled: finalIsAutoRetryEnabled,
          }
        : {}),
    };
    await dispatch(createAssignment({ data }));
    alert(`${worksheet.title} 과제가 출제되었습니다.`);
    onClose();
  };

  // step에 따라 다른 UI 렌더링
  const renderContent = () => {
    if (currentStep === "paragraph")
      return (
        <ParagraphSelectionPage
          worksheet={worksheet}
          onClose={onClose}
          onClickNext={() => {
            if (selectedParagraphs.length === 0) {
              return alert("지문을 선택해주세요.");
            }
            if (worksheet.type === "analysis") {
              handleStep("type");
            } else if (worksheet.type === "comprehensive") {
              handleStep("date");
            } else if (worksheet.type === "paragraph") {
              handleStep("date");
            }
          }}
          selectedParagraphs={selectedParagraphs}
          setSelectedParagraphs={setSelectedParagraphs}
          selectedTypes={selectedTypes}
          handleTypeClick={handleTypeClick}
          isRetryEnabled={isRetryEnabled}
          setIsRetryEnabled={setIsRetryEnabled}
          isAutoRetryEnabled={isAutoRetryEnabled}
          setIsAutoRetryEnabled={setIsAutoRetryEnabled}
          maxRetries={maxRetries}
          setMaxRetries={setMaxRetries}
        />
      );
    if (currentStep === "type")
      return (
        <TypeSelectionPage
          worksheet={worksheet}
          onClose={onClose}
          onClickNext={() => {
            if (selectedTypes.length === 0) {
              return alert("출제 유형을 선택해주세요.");
            }
            if (selectedTypes.some((type) => type.key === "syntax_analysis")) {
              if (
                selectedTypes.find((type) => type.key === "syntax_analysis")
                  .level.key === ""
              )
                return alert("구문분석 난이도를 선택해주세요.");
            }
            if (selectedTypes.some((type) => type.key === "sentence_test")) {
              if (
                selectedTypes.find((type) => type.key === "sentence_test")
                  .options.length === 0
              )
                return alert("문장학습 유형을 선택해주세요.");
            }
            if (worksheet.type === "comprehensive") {
              handleStep("paragraph");
            } else {
              handleStep("date");
            }
          }}
          selectedTypes={selectedTypes}
          setSelectedTypes={setSelectedTypes}
        />
      );
    if (currentStep === "date")
      return (
        <DateSelectionPage
          onClose={onClose}
          onClickNext={() => handleStep("student")}
          selectedDate={selectedDate}
          handleDateSelect={handleDateSelect}
          hideAfterClosed={hideAfterClosed}
          setHideAfterClosed={setHideAfterClosed}
        />
      );
    if (currentStep === "student")
      return (
        <StudentSelectionPage
          onClose={onClose}
          handleAddStudents={() => {
            handleCreateAssignment();
          }}
          selectedStudents={selectedStudents}
          setSelectedStudents={setSelectedStudents}
          selectedDate={selectedDate}
        />
      );
    if (currentStep === "title")
      return (
        <TitleSelectionPage
          onClose={onClose}
          onClickNext={() => {
            if (worksheet.type === "paragraph") {
              handleStep("paragraph");
            } else if (
              ["vocabulary", "comprehensive", "sentence", "grammar"].includes(worksheet.type)
            ) {
              setCurrentStep("type");
            } else {
              handleStep("paragraph");
            }
          }}
        />
      );
  };

  return <Modal>{renderContent()}</Modal>;
};

export default WorksheetModal;
